export interface MfRemoteSpec {

  /** Webpack `ModuleFederationPlugin.name` — the container key on `window`. */

  federationName: string;

  /** Key from webpack `exposes`. */

  module: string;

  /** Path segment under the origin, e.g. `budget_review_module`. */

  assetPath: string;

  /** Local webpack-dev-server port. */

  localPort: number;

  /** Sibling repo folder name (also the `start-env` prompt label). */

  repoName: string;

}



/**

 * Single host catalog for MF remotes. Add a remote here — URLs, local

 * defaults, the loader registry, and `start-env` prompts all derive from it.

 */

export const MF_REMOTES = {

  budgetReview: {

    federationName: "toorakBudgetReview",

    module: "./BudgetReviewWrapper",

    assetPath: "budget_review_module",

    localPort: 3008,

    repoName: "budget-review-module"

  },

  budgetReconcile: {

    federationName: "OPBudgetReconcile",

    module: "./InspectionBudget",

    assetPath: "budget_reconcile_module",

    localPort: 3009,

    repoName: "budget-reconcile-sdk"

  }

} as const satisfies Record<string, MfRemoteSpec>;



export type RemoteUrlKey = keyof typeof MF_REMOTES;

export type RemoteUrls = Record<RemoteUrlKey, string>;



export function listRemoteKeys(): RemoteUrlKey[] {

  return Object.keys(MF_REMOTES) as RemoteUrlKey[];

}



export function mapRemoteKeys<T>(

  mapper: (key: RemoteUrlKey) => T

): Record<RemoteUrlKey, T> {

  const result = {} as Record<RemoteUrlKey, T>;



  listRemoteKeys().forEach((key) => {

    result[key] = mapper(key);

  });



  return result;

}



export function buildRemoteEntryUrl(origin: string, assetPath: string): string {

  return `${origin.replace(/\/$/, "")}/${assetPath.replace(

    /^\//,

    ""

  )}/remoteEntry.js`;

}



/**
 * Resolves the origin every deployed remote's remoteEntry.js is fetched
 * from. All remotes are same-origin with Toorak Connect's own host (a
 * path-matcher rule on trk-web-app-{env}-lb routes each remote's assetPath
 * to the host's bucket — see AM-7118) — there is no per-remote or
 * dedicated-subdomain origin anymore.
 */

export function resolveDeployedRemoteOrigin(envHostSegment: string): string {

  return `https://${envHostSegment}.toorakcapital.info`;

}

/** Prod remote hosts use `www` (not `prod`). */

export function buildDeployedMfRemotes(envHostSegment: string): RemoteUrls {

  const origin = resolveDeployedRemoteOrigin(envHostSegment);

  return mapRemoteKeys((key) =>

    buildRemoteEntryUrl(origin, MF_REMOTES[key].assetPath)

  );

}

export const DEFAULT_LOCAL_MF_REMOTE_ORIGINS: Record<RemoteUrlKey, string> =

  mapRemoteKeys((key) => `http://localhost:${MF_REMOTES[key].localPort}`);



export const MF_REMOTE_META: Record<

  RemoteUrlKey,

  { repoName: string; port: number }

> = mapRemoteKeys((key) => ({

  repoName: MF_REMOTES[key].repoName,

  port: MF_REMOTES[key].localPort

}));



export function remoteEnvVarName(remoteKey: RemoteUrlKey): string {

  const snake = remoteKey.replace(/([A-Z])/g, "_$1").toUpperCase();

  return `VITE_LOCAL_MF_${snake}`;

}



export function resolveLocalMfRemoteOrigins(): Record<RemoteUrlKey, string> {

  return mapRemoteKeys((remoteKey) => {

    const envValue = process.env[remoteEnvVarName(remoteKey)];

    return envValue || DEFAULT_LOCAL_MF_REMOTE_ORIGINS[remoteKey];

  });

}



export function buildLocalMfRemotes(

  origins: Record<RemoteUrlKey, string> = resolveLocalMfRemoteOrigins()

): RemoteUrls {

  return mapRemoteKeys((remoteKey) =>

    buildRemoteEntryUrl(origins[remoteKey], MF_REMOTES[remoteKey].assetPath)

  );

}


